import { useState, useEffect } from 'react';
import api, { handleApiError } from '../services/api';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { ShoppingCart, Search, Plus, Minus, Trash2, X, Printer, CheckCircle, Package } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SalesNewPage() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('nominal');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [cashAmount, setCashAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  // Every sale is booked against a warehouse; the API rejects the request when
  // it is missing.
  const [warehouseId, setWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    // The lookups are independent: one denied endpoint must not blank the whole
    // page, so each is settled separately.
    const [prodRes, custRes, pmRes, whRes] = await Promise.allSettled([
      api.get('/products', { params: { limit: 100, is_active: true } }),
      api.get('/customers', { params: { limit: 100 } }),
      api.get('/payment-methods'),
      api.get('/warehouses', { params: { limit: 100 } })
    ]);

    if (prodRes.status === 'fulfilled') {
      const list = prodRes.value.data.data?.products || prodRes.value.data.data || [];
      setAllProducts(list);
      setProducts(list);
    }
    if (custRes.status === 'fulfilled') {
      setCustomers(custRes.value.data.data?.customers || custRes.value.data.data || []);
    }
    if (pmRes.status === 'fulfilled') {
      const list = pmRes.value.data.data?.payment_methods || pmRes.value.data.data || [];
      setPaymentMethods(list);
      if (list.length > 0) setPaymentMethod(String(list[0].id));
    }
    if (whRes.status === 'fulfilled') {
      const list = whRes.value.data.data?.warehouses || whRes.value.data.data || [];
      setWarehouses(list);
      if (list.length > 0) setWarehouseId(String(list[0].id));
    }

    const failure = [prodRes, custRes, pmRes, whRes].find((r) => r.status === 'rejected');
    if (failure) {
      toast.error(failure.reason?.response?.data?.message || 'Sebagian data gagal dimuat.');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (search.trim() === '') {
      setProducts(allProducts);
    } else {
      const q = search.toLowerCase();
      setProducts(
        allProducts.filter(
          p =>
            p.name?.toLowerCase().includes(q) ||
            p.sku?.toLowerCase().includes(q) ||
            p.barcode?.toLowerCase().includes(q)
        )
      );
    }
  }, [search, allProducts]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        sku: product.sku,
        selling_price: product.selling_price || 0,
        qty: 1,
        stock: product.stock || 0
      }];
    });
  };

  const updateQty = (productId, qty) => {
    if (qty < 1) return;
    setCart(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, qty: parseInt(qty) || 1 } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.selling_price * item.qty), 0);
  };

  const calculateDiscount = () => {
    if (discountType === 'percentage') {
      return (calculateSubtotal() * (parseFloat(discount) || 0)) / 100;
    }
    return parseFloat(discount) || 0;
  };

  const calculateTax = () => {
    return (calculateSubtotal() - calculateDiscount()) * 0.1;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateTax();
  };

  const selectedPaymentMethod = paymentMethods.find(
    (m) => String(m.id) === String(paymentMethod)
  );
  // The select holds a payment-method id once the lookup loads and a plain
  // name ('cash') before that, so cash detection must handle both shapes.
  const isCash = selectedPaymentMethod
    ? selectedPaymentMethod.type === 'cash'
    : paymentMethod === 'cash';

  const calculateChange = () => {
    if (!isCash) return 0;
    return (parseFloat(cashAmount) || 0) - calculateTotal();
  };

  const handleCompleteSale = async () => {
    if (cart.length === 0) {
      toast.error('Keranjang masih kosong');
      return;
    }
    if (!warehouseId) {
      toast.error('Gudang belum tersedia. Hubungi admin untuk menambahkan gudang.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomerId || null,
        warehouse_id: warehouseId,
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.qty,
          unit_price: item.selling_price
        })),
        discount: calculateDiscount(),
        tax_amount: calculateTax(),
        ...(selectedPaymentMethod
          ? { payment_method_id: selectedPaymentMethod.id }
          : { payment_method: paymentMethod }),
        cash_amount: isCash ? parseFloat(cashAmount) || 0 : calculateTotal()
      };
      const res = await api.post('/sales', payload);
      if (res.data.success) {
        const sale = res.data.data?.sale || res.data.data || { invoice_number: res.data.invoice_number };
        setCompletedSale(sale);
        setCart([]);
        setSelectedCustomerId('');
        setDiscount(0);
        setCashAmount('');
        toast.success('Transaksi berhasil!');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const resetSale = () => {
    setCompletedSale(null);
    setCart([]);
    setSelectedCustomerId('');
    setDiscount(0);
    setDiscountType('nominal');
    setPaymentMethod('cash');
    setCashAmount('');
    setShowConfirm(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">Transaksi Baru</h1>
            <p className="page-subtitle">Point of Sale</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <div className="skeleton h-24 w-24 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-40" />
                    <div className="skeleton h-4 w-24" />
                    <div className="skeleton h-4 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (completedSale) {
    return (
      <div className="max-w-lg mx-auto space-y-6 pt-8">
        <div className="card text-center">
          <div className="card-body py-12">
            <div className="w-20 h-20 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-success-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Transaksi Berhasil!</h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 mb-2">Invoice: {completedSale.sale_number || completedSale.invoice_number || completedSale.number}</p>
            <p className="text-3xl font-semibold text-primary-800 mb-8">{formatCurrency(completedSale.total || calculateTotal())}</p>

            <div className="flex gap-3 justify-center">
              <button onClick={handlePrint} className="btn-primary">
                <Printer className="h-4 w-4 mr-2" /> Cetak Invoice
              </button>
              <button onClick={resetSale} className="btn-secondary">
                Transaksi Baru
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <span className="chip mb-3">
          <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
          Point of Sale
        </span>
        <h1 className="text-xl tracking-[-0.3px] text-surface-900 dark:text-white sm:text-[1.375rem]">Transaksi Baru</h1>
        <p className="mt-1 text-sm text-surface-500 dark:text-surface-400">Pilih produk untuk menambahkan ke keranjang.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Products */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search */}
          <div className="card">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10 pr-10 py-3 text-lg"
                  placeholder="Cari nama produk, SKU, atau barcode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {products.length === 0 ? (
              <div className="col-span-full py-16 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">Produk tidak ditemukan</p>
              </div>
            ) : (
              products.map(product => (
                <button
                  key={product.id}
                  onClick={() => addToCart(product)}
                  disabled={(product.stock || 0) <= 0}
                  className={`card overflow-hidden p-0 text-left transition-all duration-200 ${
                    (product.stock || 0) <= 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-card-hover dark:hover:border-primary-700'
                  }`}
                >
                  <div className="flex aspect-square w-full items-center justify-center border-b border-surface-100 bg-surface-50 dark:border-surface-800 dark:bg-surface-950">
                    <Package className="h-8 w-8 text-primary-400 dark:text-primary-500" strokeWidth={1.5} />
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-medium text-surface-900 dark:text-white">{product.name}</p>
                    <p className="mt-0.5 text-[11px] text-surface-400">SKU: {product.sku}</p>
                    <div className="mt-2 flex items-baseline justify-between gap-2">
                      <p className="text-sm font-medium text-surface-900 tabular-nums dark:text-white">{formatCurrency(product.selling_price)}</p>
                      <p className={`flex-shrink-0 text-[11px] ${(product.stock || 0) > 0 ? 'text-surface-400' : 'text-danger-600 dark:text-danger-400'}`}>
                        Stok {formatNumber(product.stock)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Cart */}
        <div className="space-y-4">
          <div className="card">
            <div className="card-body">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Keranjang
                {cart.length > 0 && (
                  <span className="badge badge-primary ml-auto">{cart.length} item</span>
                )}
              </h3>

              {/* Customer Select */}
              <div className="mb-4">
                <label className="label">Pelanggan (opsional)</label>
                <select className="input" value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
                  <option value="">Umum / Non-Pelanggan</option>
                  {customers.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>

              {/* Warehouse Select — the API requires it on every sale. */}
              <div className="mb-4">
                <label className="label">Gudang</label>
                <select
                  className="input"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  disabled={warehouses.length === 0}
                  required
                >
                  {warehouses.length === 0 ? (
                    <option value="">Belum ada gudang</option>
                  ) : (
                    warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))
                  )}
                </select>
                {warehouses.length === 0 && (
                  <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">
                    Transaksi tidak dapat disimpan tanpa gudang.
                  </p>
                )}
              </div>

              {/* Cart Items */}
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">Keranjang masih kosong</p>
                  <p className="text-xs text-gray-400">Pilih produk dari daftar</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {cart.map((item) => (
                    <div key={item.product_id} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">{formatCurrency(item.selling_price)}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateQty(item.product_id, item.qty - 1)}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <input
                          type="number"
                          className="input w-14 text-center text-sm py-1"
                          value={item.qty}
                          min="1"
                          max={item.stock}
                          onChange={(e) => updateQty(item.product_id, e.target.value)}
                        />
                        <button
                          onClick={() => updateQty(item.product_id, item.qty + 1)}
                          disabled={item.qty >= item.stock}
                          className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white w-20 text-right">
                        {formatCurrency(item.selling_price * item.qty)}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="p-1 text-danger-500 hover:text-danger-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Totals */}
          {cart.length > 0 && (
            <div className="card">
              <div className="card-body space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal</span>
                  <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                </div>

                {/* Discount */}
                <div>
                  <label className="label text-xs">Diskon</label>
                  <div className="flex gap-2">
                    <select
                      className="input w-28"
                      value={discountType}
                      onChange={(e) => { setDiscountType(e.target.value); setDiscount(0); }}
                    >
                      <option value="nominal">Nominal</option>
                      <option value="percentage">Persen</option>
                    </select>
                    <input
                      type="number"
                      className="input flex-1"
                      placeholder={discountType === 'percentage' ? '0%' : '0'}
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      min="0"
                    />
                  </div>
                  {discount > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(calculateDiscount())}</p>
                  )}
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Tax (10%)</span>
                  <span className="font-medium">{formatCurrency(calculateTax())}</span>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 flex justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                  <span className="font-semibold text-lg text-primary-800">{formatCurrency(calculateTotal())}</span>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="label">Metode Pembayaran</label>
                  <select className="input" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                    {paymentMethods.length > 0 ? (
                      paymentMethods.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))
                    ) : (
                      <>
                        <option value="cash">Tunai</option>
                        <option value="transfer">Transfer</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Cash Amount & Change */}
                {isCash && (
                  <div>
                    <label className="label">Jumlah Tunai</label>
                    <input
                      type="number"
                      className="input"
                      placeholder="Masukkan jumlah tunai"
                      value={cashAmount}
                      onChange={(e) => setCashAmount(e.target.value)}
                      min="0"
                    />
                    {parseFloat(cashAmount) > 0 && (
                      <div className={`mt-1 text-sm font-semibold ${calculateChange() >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
                        Kembali: {formatCurrency(calculateChange())}
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={() => setShowConfirm(true)}
                  disabled={saving}
                  className="btn-primary w-full py-3 text-lg"
                >
                  {saving ? 'Memproses...' : 'Selesaikan Transaksi'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Konfirmasi Transaksi</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">{item.name} x {item.qty}</span>
                    <span className="font-medium">{formatCurrency(item.selling_price * item.qty)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{formatCurrency(calculateSubtotal())}</span>
                </div>
                {calculateDiscount() > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Diskon</span>
                    <span className="text-danger-600">-{formatCurrency(calculateDiscount())}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax</span>
                  <span>{formatCurrency(calculateTax())}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-1">
                  <span>Total</span>
                  <span className="text-primary-800">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowConfirm(false)} className="btn-secondary flex-1">Batal</button>
                <button onClick={handleCompleteSale} disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Memproses...' : 'Konfirmasi & Selesai'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}